import {
    Divider,
    List,
    ListItem,
    Text,
    ThemeColorVariable,
    ThemeSpaceVariable,
    useDisclosure,
} from "@caspeco/casper-ui-library";
import { useWebBooking } from "api/api-hooks/use-web-booking";
import ConfirmationDialog from "components/confirm-dialog";
import getActivities, {
    ActivityMenuPair,
} from "components/selection-components/summary/activity-menu-pair";
import {
    getPaymentBadgeTranslationKeyForPaymentRules,
    PaymentBadgeTranslationKey,
} from "components/selection-components/select-activity/activity-badges/activity-badges-helper";
import { useRemoveActivity } from "components/selection-components/summary/use-remove-activity";
import { useSectionNameMapping } from "components/selection-components/summary/use-section-name-mapping";
import { SummaryEntry, SummaryRow } from "components/selection-components/summary/summary-row";
import {
    getStandaloneConfirmedMenuEntries,
    getStandaloneConfirmedMenuSummary,
} from "components/selection-components/summary/summary-menu-entries";
import { urlBuilder } from "search-params/url-builder";
import { getLocalizedValue } from "helpers/language-helper";
import { useCurrencyCode } from "hooks/use-currency-code";
import { usePaymentRule } from "hooks/use-payment-rule";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { PaymentRule } from "types/payment";
import { WebRule } from "types/web-rule";
import { ImageServiceAspectRatio } from "types/image-service-aspect-ratio";
import { hasUnpaidPrepayment } from "utils/booking";
import { getActivityPriceText, getActivityVisiblePrice } from "utils/activity-utils";
import { useActivityPrices } from "hooks/use-activity-prices";
import { checkIsDetailsForm } from "routes";

interface ISummary {
    /**
     * Used for open an activity, if it is not present we assume we are at details form
     */
    openActivity?: (ruleId: number) => void;
    openExperience?: (experienceId: number) => void;
    onCloseModal?: () => void;
    /**
     * When provided, shows edit and delete buttons on the details form
     */
    onEditActivity?: (ruleId: number) => void;
    onEditExperience?: (experienceId: number) => void;
}

const THUMBNAIL_PARAMS = {
    AspectRatio: ImageServiceAspectRatio.OneByOne,
    Type: "webp",
    Width: "64",
};

function buildImageUrl(imageRef: string | undefined): string {
    if (!imageRef) return "";
    return urlBuilder(
        `${import.meta.env.VITE_APP_IMAGE_SERVICE_URL}/${imageRef}`,
        THUMBNAIL_PARAMS
    );
}

const Summary = ({
    openActivity,
    openExperience,
    onCloseModal,
    onEditActivity,
    onEditExperience,
}: ISummary) => {
    const { isOpen, onClose: onCloseDialog, onOpen } = useDisclosure();
    const { data: reservation } = useWebBooking();
    const [activityToDelete, setActivityToDelete] = useState<ActivityMenuPair | null>(null);
    const [experienceToDelete, setExperienceToDelete] = useState<{
        id: number;
        name: string;
        activities: ActivityMenuPair[];
    } | null>(null);
    const { removeActivities } = useRemoveActivity(onCloseModal, onCloseDialog);
    const { t } = useTranslation();
    const paymentRule = usePaymentRule();
    const sectionNameMap = useSectionNameMapping();
    const activityMenuPairs = reservation
        ? getActivities(reservation, reservation.webBookingRules, sectionNameMap)
        : [];
    const isDetailsForm = checkIsDetailsForm(window.location.pathname);

    const ruleMap = new Map<number, WebRule>();
    reservation?.webBookingRules?.forEach((rule) => {
        ruleMap.set(rule.id, rule);
    });

    const { totalPrice } = useActivityPrices();
    const currency = useCurrencyCode();
    const showPrepaySumSection = isDetailsForm && totalPrice !== 0;
    const shouldShowPrepayText = paymentRule !== PaymentRule.NoShowFee;
    const summaryPaymentBadgeTranslationKey: PaymentBadgeTranslationKey | null =
        paymentRule === PaymentRule.Prepayment
            ? "activitySummary.paymentBadge.prepayment"
            : getPaymentBadgeTranslationKeyForPaymentRules(
                  activityMenuPairs.map((activity) => activity.paymentRule)
              );

    const experienceMap = new Map<number, ActivityMenuPair[]>();
    const regularActivities: ActivityMenuPair[] = [];

    activityMenuPairs.forEach((activity) => {
        const activityRule = ruleMap.get(activity.ruleId);
        if (activityRule?.experienceId) {
            const existing = experienceMap.get(activityRule.experienceId) || [];
            existing.push(activity);
            experienceMap.set(activityRule.experienceId, existing);
        } else {
            regularActivities.push(activity);
        }
    });

    const standaloneMenuSummary = reservation
        ? getStandaloneConfirmedMenuSummary(reservation)
        : null;
    const standaloneMenuFallbackEntries =
        reservation && standaloneMenuSummary && regularActivities.length === 0
            ? getStandaloneConfirmedMenuEntries(reservation)
            : [];

    const entries: SummaryEntry[] = [
        ...Array.from(experienceMap.entries())
            .filter(([, activities]) => activities.length > 0)
            .map(([experienceId, activities]) => {
                const localizedName = getLocalizedValue(
                    activities.at(0)?.rule?.experienceName || {}
                );
                const onEdit = openExperience
                    ? () => openExperience(experienceId)
                    : onEditExperience
                      ? () => onEditExperience(experienceId)
                      : undefined;
                return {
                    key: `experience-${experienceId}`,
                    name: activities.at(0)?.rule?.experienceName ?? {},
                    imageUrl: buildImageUrl(activities.at(0)?.rule?.experienceImage1x1),
                    startTime: activities[0].start,
                    endTime: activities[activities.length - 1].end
                        .clone()
                        .subtract(
                            activities[activities.length - 1].rule?.recoupTime ?? 0,
                            "minutes"
                        ),
                    price: activities.reduce(
                        (sum, a) => sum + a.resourceTotalPrice + a.menuPrice,
                        0
                    ),
                    priceText: null,
                    paymentRule: undefined,
                    groups: undefined,
                    children: activities,
                    isExperience: true,
                    trackAttribute: "summary-remove-experience",
                    onEdit,
                    onDelete: onEdit
                        ? () => {
                              setExperienceToDelete({
                                  id: experienceId,
                                  name: localizedName,
                                  activities,
                              });
                              onOpen();
                          }
                        : undefined,
                };
            }),
        ...regularActivities.map((activity, index) => {
            const activityWithStandaloneMenu =
                index === 0 && standaloneMenuSummary
                    ? {
                          ...activity,
                          groups: [...(activity.groups ?? []), ...standaloneMenuSummary.groups],
                          menuPrice: activity.menuPrice + standaloneMenuSummary.price,
                      }
                    : activity;
            const onEdit = openActivity
                ? () => openActivity(activity.ruleId ?? 0)
                : onEditActivity
                  ? () => onEditActivity(activity.ruleId ?? 0)
                  : undefined;
            const priceText =
                activityWithStandaloneMenu.paymentRule === PaymentRule.Prepayment
                    ? getActivityPriceText(activityWithStandaloneMenu, reservation?.guests ?? 0)
                    : null;
            return {
                key: `activity-${activity.ruleId}`,
                name: { sv: activity.sectionName ?? "" },
                imageUrl: buildImageUrl(sectionNameMap.get(activity.ruleId)?.image),
                startTime: activity.start,
                endTime: activity.end.clone().subtract(activity.rule?.recoupTime ?? 0, "minutes"),
                price: getActivityVisiblePrice(activityWithStandaloneMenu),
                priceText,
                paymentRule: activity.paymentRule,
                groups: activityWithStandaloneMenu.groups,
                children: [],
                isExperience: false,
                trackAttribute: "summary-remove-activity",
                onEdit,
                onDelete: onEdit
                    ? () => {
                          setActivityToDelete(activity);
                          onOpen();
                      }
                    : undefined,
            };
        }),
        ...standaloneMenuFallbackEntries,
    ].sort((a, b) => a.startTime.valueOf() - b.startTime.valueOf());

    const isLastEntry = entries.length === 1;

    const showDividerAfter = (index: number) => index < entries.length - 1;
    return (
        <>
            <List spacing={ThemeSpaceVariable.None}>
                {entries.map((entry, index) => (
                    <SummaryRow
                        key={entry.key}
                        entry={entry}
                        isDetailsForm={isDetailsForm}
                        showDivider={showDividerAfter(index)}
                    />
                ))}
            </List>
            {showPrepaySumSection && (
                <>
                    <Divider />
                    <List
                        display="flex"
                        flexDirection="column"
                        gap={ThemeSpaceVariable.XSmall}
                        py={ThemeSpaceVariable.Medium}
                    >
                        {summaryPaymentBadgeTranslationKey && (
                            <ListItem display="flex">
                                <Text fontWeight="medium">
                                    {t(summaryPaymentBadgeTranslationKey)}
                                </Text>
                            </ListItem>
                        )}
                        {shouldShowPrepayText &&
                            reservation &&
                            hasUnpaidPrepayment(reservation) && (
                                <ListItem display="flex" justifyContent="space-between">
                                    <Text>{t("menuSummary.subtotal")}</Text>
                                    <Text>
                                        {t("currency.onlyAmount", {
                                            value: reservation?.chargeShouldPayAmount,
                                            display: "symbol",
                                        })}
                                    </Text>
                                </ListItem>
                            )}
                        <ListItem display="flex" gap={ThemeSpaceVariable.XSmall}>
                            <Text fontWeight="bold">{`${t("menuSummary.totalCost")} ${currency}`}</Text>
                            <Text color={ThemeColorVariable.OnSurfaceSubdued}>
                                {t("menuSummary.inkVat")}
                            </Text>
                            <Text fontWeight="bold" marginLeft="auto">
                                {t("currency.onlyAmount", {
                                    value: totalPrice,
                                    display: "symbol",
                                })}
                            </Text>
                        </ListItem>
                    </List>
                </>
            )}
            <ConfirmationDialog
                closeOnOverlayClick={true}
                primaryButtonText={t("summary.remove")}
                secondaryButtonText={t("summary.cancel")}
                isOpen={isOpen}
                title={t("summary.removeActivityX", {
                    activityName: experienceToDelete
                        ? experienceToDelete.name
                        : activityToDelete?.articleGroupName,
                })}
                message={t(
                    isDetailsForm && isLastEntry
                        ? "summary.areYouSureRemoveLast"
                        : "summary.areYouSureRemove",
                    {
                        activityName: experienceToDelete
                            ? experienceToDelete.name
                            : activityToDelete?.articleGroupName,
                    }
                )}
                onClose={() => {
                    onCloseDialog();
                    setExperienceToDelete(null);
                    setActivityToDelete(null);
                }}
                primaryAction={() => {
                    onCloseDialog();
                    if (experienceToDelete) {
                        void removeActivities(
                            experienceToDelete.activities.map((a) => ({
                                ruleId: a.ruleId,
                                sectionId: a.sectionId,
                            }))
                        );
                        setExperienceToDelete(null);
                    } else if (activityToDelete?.ruleId !== undefined) {
                        void removeActivities([
                            {
                                ruleId: activityToDelete.ruleId,
                                sectionId: activityToDelete.sectionId,
                            },
                        ]);
                        setActivityToDelete(null);
                    }
                }}
                secondaryAction={() => {
                    onCloseDialog();
                    setExperienceToDelete(null);
                    setActivityToDelete(null);
                }}
            />
        </>
    );
};

export default Summary;
